/**
 * useSubdomainDetection — reads + clears the remembered subdomain from
 * caller-supplied persistence. Caller is responsible for the actual storage
 * (we can't assume localStorage shape). Returns stable handler identities
 * across re-renders.
 *
 * @module @puffin-core/login-shell/hooks/useSubdomainDetection
 */

import { useState, useCallback, useEffect } from 'react';

export function useSubdomainDetection(persistence) {
  const [storedSubdomain, setStoredSubdomain] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const readSubdomain = useCallback(() => {
    if (!persistence?.getStoredSubdomain) {
      setStoredSubdomain(null);
      setIsLoading(false);
      return;
    }
    // No catch (SWALLOWED-CATCH-BURNDOWN-001 U3): both shipped persistences
    // (`createSubdomainPersistence`, pos-web's StorageService-backed one) absorb the browser
    // refusing storage themselves, so a throw here is a defect — it used to read as "no
    // remembered company".
    setStoredSubdomain(persistence.getStoredSubdomain() || null);
    setIsLoading(false);
  }, [persistence]);

  const clearSubdomain = useCallback(() => {
    if (persistence?.clearStoredSubdomain) {
      persistence.clearStoredSubdomain();
    }
    setStoredSubdomain(null);
  }, [persistence]);

  useEffect(() => {
    readSubdomain();
  }, [readSubdomain]);

  const hasStoredSubdomain = Boolean(storedSubdomain && storedSubdomain.length > 0);

  return {
    storedSubdomain,
    hasStoredSubdomain,
    isLoading,
    clearSubdomain,
    refresh: readSubdomain,
  };
}

export default useSubdomainDetection;
